import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { UIIncident } from '../../../../../../../api/teamMetricsApi';
import { IncidentsSummary } from './IncidentsSummary';
import { IncidentHistoryChart } from './IncidentHistoryChart';
import { IncidentsTable } from './IncidentsTable';
import { FilterChips } from './FilterChips';
import { useIncidentFilters } from './useIncidentFilters';
import {
  aggregateIncidents,
  computeIncidentHistory,
  countByField,
  computeResponseQualityMetrics,
} from './aggregateIncidents';

const useStyles = makeStyles((theme) => ({
  root: {},
  divider: {
    borderTop: '1px solid #E2E8F0',
    margin: theme.spacing(4, 0),
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: '#EBF8FF',
    border: '1px solid #BEE3F8',
    borderRadius: 4,
  },
  infoIcon: {
    fontSize: 16,
    color: '#3182CE',
    marginTop: 2,
  },
  infoText: {
    fontSize: 12,
    color: '#2B6CB0',
    lineHeight: 1.5,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8),
    textAlign: 'center',
    backgroundColor: '#F7FAFC',
    borderRadius: 8,
    border: '1px dashed #CBD5E0',
  },
  emptyIcon: {
    fontSize: 64,
    color: '#CBD5E0',
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#4A5568',
    marginBottom: theme.spacing(1),
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#8E9299',
    maxWidth: 400,
  },
}));

interface IncidentsContentProps {
  incidents: UIIncident[];
  columnHeaders: string[];
  startDate: string;
  teamName: string;
  searchParams?: URLSearchParams;
  setSearchParams?: (params: URLSearchParams, options?: { replace?: boolean }) => void;
}

export const IncidentsContent: React.FC<IncidentsContentProps> = ({
  incidents,
  columnHeaders,
  startDate,
  teamName,
  searchParams,
  setSearchParams,
}) => {
  const classes = useStyles();

  const {
    filters,
    filteredIncidents,
    availableValues,
    getAvailableValuesForColumn,
    addFilter,
    removeFilter,
  } = useIncidentFilters({
    incidents,
    initialTeamFilter: teamName,
    searchParams,
    setSearchParams,
  });

  const incidentSummary = useMemo(
    () => aggregateIncidents(filteredIncidents, columnHeaders, startDate),
    [filteredIncidents, columnHeaders, startDate]
  );

  const orgIncidentHistory = useMemo(
    () => computeIncidentHistory(incidents, startDate),
    [incidents, startDate]
  );

  const orgSeverityDistribution = useMemo(
    () => countByField(incidents, 'SEV Level V2'),
    [incidents]
  );

  const responseQualityMetrics = useMemo(
    () => computeResponseQualityMetrics(filteredIncidents),
    [filteredIncidents]
  );

  if (incidents.length === 0) {
    return (
      <Box className={classes.root}>
        <Box className={classes.emptyState}>
          <WarningAmberIcon className={classes.emptyIcon} />
          <Typography className={classes.emptyTitle}>No Incident Data Available</Typography>
          <Typography className={classes.emptySubtitle}>
            There are no incidents recorded in the selected time period. Incident data is sourced
            from the Incident Tracker spreadsheet.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.infoBox} sx={{ mb: 2 }}>
        <InfoOutlinedIcon className={classes.infoIcon} />
        <Typography className={classes.infoText}>
          <strong>Data Source:</strong> Incident data is sourced from the Incident Tracker
          Google Spreadsheet. Click on an incident to view details in the source document.
        </Typography>
      </Box>

      <FilterChips
        filters={filters}
        availableValues={availableValues}
        getAvailableValuesForColumn={getAvailableValuesForColumn}
        onRemoveFilter={removeFilter}
        onAddFilter={addFilter}
      />

      {filteredIncidents.length === 0 ? (
        <Box className={classes.emptyState}>
          <WarningAmberIcon className={classes.emptyIcon} />
          <Typography className={classes.emptyTitle}>No Matching Incidents</Typography>
          <Typography className={classes.emptySubtitle}>
            No incidents match the current filters. Try removing some filters to see more results.
          </Typography>
        </Box>
      ) : (
        <>
          <IncidentsSummary
            totalIncidents={incidentSummary.totalIncidents}
            orgTotalIncidents={incidents.length}
            severityDistribution={incidentSummary.severityDistribution}
            orgSeverityDistribution={orgSeverityDistribution}
            ttdMinutes={responseQualityMetrics.ttdMinutes}
            ttdDistribution={responseQualityMetrics.ttdDistribution}
            ttrMinutes={responseQualityMetrics.ttrMinutes}
            ttrDistribution={responseQualityMetrics.ttrDistribution}
            detectionMismatchCount={responseQualityMetrics.detectionMismatchCount}
            detectionMismatchTotal={responseQualityMetrics.detectionMismatchTotal}
            detectionMismatches={responseQualityMetrics.detectionMismatches}
            detectionMatches={responseQualityMetrics.detectionMatches}
            mttrBySeverity={responseQualityMetrics.mttrBySeverity}
          />

          <Box className={classes.divider} />

          <IncidentHistoryChart
            data={incidentSummary.incidentHistory}
            orgData={orgIncidentHistory}
            startDate={startDate}
          />

          <IncidentsTable
            incidents={incidentSummary.incidents}
            columnHeaders={incidentSummary.incidentColumnHeaders}
          />
        </>
      )}
    </Box>
  );
};

export default IncidentsContent;
